import { AngebotSparte, PartnerRefAngebotBeziehungRollen } from '@mng/sales-types';
import { Antragdata, Person } from '../../antrag.model.types';
import { hatAntragSpartenAngebotLv } from '../logic/antrag-model.logic';
import { IAntragFragen, IRollenFeatures, ITarifFeature, IZusatzFlagsFuerSonderBehandlung } from './antrag-fragen';
import { isPersonInSparte } from './antrag-fragen.logic';
import { gvRollenFeatures } from './gv-rollen-features';
import { lvRollenFeatures } from './lv-rollen-features';
import { ILvDefinitionen, lvTarifFeatures } from './lv-tarif-features';
import { uvRollenFeatures } from './uv-rollen-features';
import { svRollenFeatures } from './sv-rollen-features';
import { bufferedLogger } from '@mng/logging';

export const PFLEGE_VS_GESUNDHEITSFRAGENG = 'LV16';
export const GPZ_BAUSTEIN_GESUNDHEITSFRAGENG = 'LV16';
export const ERLEBEN_MIT_FRAGEN = 'ERLEBEN_MIT_FRAGEN';
export const PBZ_BAUSTEIN_GESUNDHEITSFRAGENG = 'PBZ_BAUSTEIN';
export const UTZ_BAUSTEIN_GESUNDHEITSFRAGENG = 'UTZ_BAUSTEIN';
export const SPARTE_GV_RELEVANT = 'GV_SPARTE';
export const SPARTE_UV_RELEVANT = 'UV_SPARTE';

export function getZusatzFragenSonderbehandlung(
  tarifSchluessel: string[],
  rollen: string[],
  lvDefinitionen: ILvDefinitionen,
  juristischePerson: boolean
): IZusatzFlagsFuerSonderBehandlung {
  const zusatzFragen: IZusatzFlagsFuerSonderBehandlung = {};
  zusatzFragen.only_paragraph_3_1_15_tarifeExistsInAntrag = getPartnerDatenSonderbehandlungFor_paragraph_3_1_15_Flag(
    tarifSchluessel,
    lvDefinitionen
  );
  return zusatzFragen;
}

export function getPartnerDatenSonderbehandlungFor_paragraph_3_1_15_Flag(
  tarifSchluessel: string[],
  lvDefinitionen: ILvDefinitionen
): boolean {
  let flag_only_3_1_15_Tarife = false;
  if (tarifSchluessel) {
    tarifSchluessel.forEach((tarif, index: number) => {
      const tarifFragenDefs = { ...lvDefinitionen.tarife[tarif].fragenDefs };
      if (index === 0) {
        flag_only_3_1_15_Tarife = tarifFragenDefs.paragraph_3_1_15;
      }
      flag_only_3_1_15_Tarife = flag_only_3_1_15_Tarife && tarifFragenDefs.paragraph_3_1_15;
    });
  }
  return flag_only_3_1_15_Tarife;
}

export function getFragenBlockDefinitionForPersonRollenGv(
  personRollen: string[],
  juristischePerson: boolean
): IAntragFragen {
  return getFragenBlockDefinitionForPersonRollen(gvRollenFeatures, personRollen, juristischePerson);
}

export function getFragenBlockDefinitionForPersonRollenUv(
  personRollen: string[],
  juristischePerson: boolean
): IAntragFragen {
  return getFragenBlockDefinitionForPersonRollen(uvRollenFeatures, personRollen, juristischePerson);
}

export function getFragenBlockDefinitionForPersonRollenSv(
  personRollen: string[],
  juristischePerson: boolean
): IAntragFragen {
  return getFragenBlockDefinitionForPersonRollen(svRollenFeatures, personRollen, juristischePerson);
}

export function getFragenBlockDefinitionForPersonRollen(
  rollenFeatures: IRollenFeatures[],
  personRollen: string[],
  juristischePerson: boolean
): IAntragFragen {
  const fragenBlockDef: { [rolle: string]: IRollenFeatures } = {};
  rollenFeatures.forEach((rollenDef) => (fragenBlockDef[rollenDef.rolle] = rollenDef));
  return getRollenFragenDefs(personRollen, fragenBlockDef, juristischePerson);
}

export function getFragenBlockDefinitionLv(
  tarifSchluessel: string[],
  rollen: string[],
  lvDefinitionen: ILvDefinitionen,
  juristischePerson: boolean
): IAntragFragen {
  let rollenResultSetFragen: IAntragFragen = {};
  let tarifResultSetFragen: IAntragFragen = {};
  const gesamtResultSetFragen: IAntragFragen = {};
  gesamtResultSetFragen.gesundheitsfragenG = new Set<string>();

  if (tarifSchluessel) {
    tarifSchluessel.forEach((tarif, index: number) => {
      const tarifFragenDefs = { ...lvDefinitionen.tarife[tarif].fragenDefs };
      if (index === 0) {
        tarifResultSetFragen = { ...tarifFragenDefs };
        tarifResultSetFragen.gesundheitsfragenG = new Set(tarifFragenDefs.gesundheitsfragenG);
      } else {
        tarifFragenDefs.gesundheitsfragenG.forEach((gfGruppe) => {
          tarifResultSetFragen.gesundheitsfragenG.add(gfGruppe);
        });
        tarifResultSetFragen.gesundheitsfragenCTFS =
          tarifResultSetFragen.gesundheitsfragenCTFS || tarifFragenDefs.gesundheitsfragenCTFS;
        tarifResultSetFragen.gesundheitsfragen =
          tarifResultSetFragen.gesundheitsfragen || tarifFragenDefs.gesundheitsfragen;
        tarifResultSetFragen.geldwaesche = tarifResultSetFragen.geldwaesche || tarifFragenDefs.geldwaesche;
        tarifResultSetFragen.bezugsrechtAbleben =
          tarifResultSetFragen.bezugsrechtAbleben || tarifFragenDefs.bezugsrechtAbleben;
        tarifResultSetFragen.bezugsrechtErleben =
          tarifResultSetFragen.bezugsrechtErleben || tarifFragenDefs.bezugsrechtErleben;
        tarifResultSetFragen.bezugsrechtKrankheit =
          tarifResultSetFragen.bezugsrechtKrankheit || tarifFragenDefs.bezugsrechtKrankheit;
        tarifResultSetFragen.paragraph_3_1_15 =
          tarifResultSetFragen.paragraph_3_1_15 || tarifFragenDefs.paragraph_3_1_15;
        tarifResultSetFragen.sensibleDaten = tarifResultSetFragen.sensibleDaten || tarifFragenDefs.sensibleDaten;
        tarifResultSetFragen.treuhaenderFrage =
          tarifResultSetFragen.treuhaenderFrage || tarifFragenDefs.treuhaenderFrage;
        tarifResultSetFragen.arbeitsStatusFrage =
          tarifResultSetFragen.arbeitsStatusFrage || tarifFragenDefs.arbeitsStatusFrage;
        tarifResultSetFragen.identifikationsnachweis =
          tarifResultSetFragen.identifikationsnachweis || tarifFragenDefs.identifikationsnachweis;
        tarifResultSetFragen.crsRelevant = tarifResultSetFragen.crsRelevant || tarifFragenDefs.crsRelevant;
        tarifResultSetFragen.fatcaGMSG = tarifResultSetFragen.fatcaGMSG || tarifFragenDefs.fatcaGMSG;
        tarifResultSetFragen.veranlagungBeratung =
          tarifResultSetFragen.veranlagungBeratung || tarifFragenDefs.veranlagungBeratung;
        tarifResultSetFragen.schriftformDatennutzung =
          tarifResultSetFragen.schriftformDatennutzung || tarifFragenDefs.schriftformDatennutzung;
        tarifResultSetFragen.bestattung = tarifResultSetFragen.bestattung || tarifFragenDefs.bestattung;
        tarifResultSetFragen.familienstand = tarifResultSetFragen.familienstand || tarifFragenDefs.familienstand;
        tarifResultSetFragen.geburtsland = tarifResultSetFragen.geburtsland || tarifFragenDefs.geburtsland;
        tarifResultSetFragen.berufsinfo = tarifResultSetFragen.berufsinfo || tarifFragenDefs.berufsinfo;
        tarifResultSetFragen.sozialVersDaten = tarifResultSetFragen.sozialVersDaten || tarifFragenDefs.sozialVersDaten;
        tarifResultSetFragen.adresse = tarifResultSetFragen.adresse || tarifFragenDefs.adresse;
        tarifResultSetFragen.nationalitaet = tarifResultSetFragen.nationalitaet || tarifFragenDefs.nationalitaet;
        tarifResultSetFragen.erhaltbasisInfoBlatt =
          tarifResultSetFragen.erhaltbasisInfoBlatt || tarifFragenDefs.erhaltbasisInfoBlatt;
      }
    });
    // juristische Person berücksichtigen
    tarifResultSetFragen.familienstand = tarifResultSetFragen.familienstand && !juristischePerson;
    tarifResultSetFragen.geburtsland = tarifResultSetFragen.geburtsland && !juristischePerson;
    tarifResultSetFragen.berufsinfo = tarifResultSetFragen.berufsinfo && !juristischePerson;
    tarifResultSetFragen.sozialVersDaten = tarifResultSetFragen.sozialVersDaten && !juristischePerson;
    tarifResultSetFragen.adresse = tarifResultSetFragen.adresse && !juristischePerson;
    tarifResultSetFragen.nationalitaet = tarifResultSetFragen.nationalitaet && !juristischePerson;
  }
  if (!rollen || rollen.length === 0) {
    return tarifResultSetFragen;
  }
  if (rollen) {
    rollenResultSetFragen = getRollenFragenDefs(rollen, lvDefinitionen.rollen, juristischePerson);
  }
  if (!tarifSchluessel || tarifSchluessel.length === 0) {
    return rollenResultSetFragen;
  }
  tarifResultSetFragen.gesundheitsfragenG.forEach((gfGruppe) => {
    gesamtResultSetFragen.gesundheitsfragenG.add(gfGruppe);
  });
  gesamtResultSetFragen.gesundheitsfragenCTFS =
    rollenResultSetFragen.gesundheitsfragenCTFS && tarifResultSetFragen.gesundheitsfragenCTFS && !juristischePerson;
  gesamtResultSetFragen.gesundheitsfragen =
    rollenResultSetFragen.gesundheitsfragen && tarifResultSetFragen.gesundheitsfragen && !juristischePerson;
  gesamtResultSetFragen.geldwaesche =
    rollenResultSetFragen.geldwaesche && tarifResultSetFragen.geldwaesche && !juristischePerson;
  gesamtResultSetFragen.bezugsrechtKrankheit =
    rollenResultSetFragen.bezugsrechtKrankheit && tarifResultSetFragen.bezugsrechtKrankheit;
  gesamtResultSetFragen.bezugsrechtErleben =
    rollenResultSetFragen.bezugsrechtErleben && tarifResultSetFragen.bezugsrechtErleben;
  gesamtResultSetFragen.bezugsrechtAbleben =
    rollenResultSetFragen.bezugsrechtAbleben && tarifResultSetFragen.bezugsrechtAbleben;
  gesamtResultSetFragen.paragraph_3_1_15 =
    rollenResultSetFragen.paragraph_3_1_15 && tarifResultSetFragen.paragraph_3_1_15;
  gesamtResultSetFragen.sensibleDaten = rollenResultSetFragen.sensibleDaten && tarifResultSetFragen.sensibleDaten;
  gesamtResultSetFragen.treuhaenderFrage =
    rollenResultSetFragen.treuhaenderFrage && tarifResultSetFragen.treuhaenderFrage;
  gesamtResultSetFragen.arbeitsStatusFrage =
    rollenResultSetFragen.arbeitsStatusFrage && tarifResultSetFragen.arbeitsStatusFrage;
  gesamtResultSetFragen.identifikationsnachweis =
    rollenResultSetFragen.identifikationsnachweis && tarifResultSetFragen.identifikationsnachweis;
  gesamtResultSetFragen.crsRelevant =
    rollenResultSetFragen.crsRelevant && tarifResultSetFragen.crsRelevant && !juristischePerson;
  gesamtResultSetFragen.fatcaGMSG =
    rollenResultSetFragen.fatcaGMSG && tarifResultSetFragen.fatcaGMSG && !juristischePerson;
  gesamtResultSetFragen.veranlagungBeratung =
    rollenResultSetFragen.veranlagungBeratung && tarifResultSetFragen.veranlagungBeratung;
  gesamtResultSetFragen.schriftformDatennutzung =
    rollenResultSetFragen.schriftformDatennutzung && tarifResultSetFragen.schriftformDatennutzung;
  gesamtResultSetFragen.bestattung = rollenResultSetFragen.bestattung && tarifResultSetFragen.bestattung;
  gesamtResultSetFragen.familienstand =
    rollenResultSetFragen.familienstand && tarifResultSetFragen.familienstand && !juristischePerson;
  gesamtResultSetFragen.geburtsland =
    rollenResultSetFragen.geburtsland && tarifResultSetFragen.geburtsland && !juristischePerson;
  gesamtResultSetFragen.berufsinfo =
    rollenResultSetFragen.berufsinfo && tarifResultSetFragen.berufsinfo && !juristischePerson;
  gesamtResultSetFragen.sozialVersDaten =
    rollenResultSetFragen.sozialVersDaten && tarifResultSetFragen.sozialVersDaten && !juristischePerson;
  gesamtResultSetFragen.adresse = rollenResultSetFragen.adresse && tarifResultSetFragen.adresse && !juristischePerson;
  gesamtResultSetFragen.nationalitaet =
    rollenResultSetFragen.nationalitaet && tarifResultSetFragen.nationalitaet && !juristischePerson;
  gesamtResultSetFragen.erhaltbasisInfoBlatt =
    rollenResultSetFragen.erhaltbasisInfoBlatt && tarifResultSetFragen.erhaltbasisInfoBlatt;
  // nur fuer nat. Person!
  return gesamtResultSetFragen;
}

export function getRollenFragenDefs(
  personRollen: string[],
  rollenFeatures: { [rolle: string]: IRollenFeatures },
  juristischePerson: boolean
): IAntragFragen {
  let rollenResultSetFragen: IAntragFragen = {};

  personRollen.forEach((rolle, index: number) => {
    const curentFragenDefs = rollenFeatures[rolle]?.fragenDefs ?? {};
    const rollenFragenDefs = { ...curentFragenDefs };
    if (index === 0) {
      rollenResultSetFragen = rollenFragenDefs;
      rollenFragenDefs.gesundheitsfragenG = new Set(rollenFragenDefs.gesundheitsfragenG);
    } else {
      rollenFragenDefs.gesundheitsfragenG?.forEach((gfGruppe) => {
        rollenResultSetFragen.gesundheitsfragenG.add(gfGruppe);
      });
      rollenResultSetFragen.gesundheitsfragenCTFS =
        rollenResultSetFragen.gesundheitsfragenCTFS || rollenFragenDefs.gesundheitsfragenCTFS;
      rollenResultSetFragen.gesundheitsfragen =
        rollenResultSetFragen.gesundheitsfragen || rollenFragenDefs.gesundheitsfragen;
      rollenResultSetFragen.geldwaesche = rollenResultSetFragen.geldwaesche || rollenFragenDefs.geldwaesche;
      rollenResultSetFragen.bezugsrechtAbleben =
        rollenResultSetFragen.bezugsrechtAbleben || rollenFragenDefs.bezugsrechtAbleben;
      rollenResultSetFragen.bezugsrechtErleben =
        rollenResultSetFragen.bezugsrechtErleben || rollenFragenDefs.bezugsrechtErleben;
      rollenResultSetFragen.bezugsrechtKrankheit =
        rollenResultSetFragen.bezugsrechtKrankheit || rollenFragenDefs.bezugsrechtKrankheit;
      rollenResultSetFragen.paragraph_3_1_15 =
        rollenResultSetFragen.paragraph_3_1_15 || rollenFragenDefs.paragraph_3_1_15;
      rollenResultSetFragen.sensibleDaten = rollenResultSetFragen.sensibleDaten || rollenFragenDefs.sensibleDaten;
      rollenResultSetFragen.treuhaenderFrage =
        rollenResultSetFragen.treuhaenderFrage || rollenFragenDefs.treuhaenderFrage;
      rollenResultSetFragen.arbeitsStatusFrage =
        rollenResultSetFragen.arbeitsStatusFrage || rollenFragenDefs.arbeitsStatusFrage;
      rollenResultSetFragen.identifikationsnachweis =
        rollenResultSetFragen.identifikationsnachweis || rollenFragenDefs.identifikationsnachweis;
      rollenResultSetFragen.crsRelevant = rollenResultSetFragen.crsRelevant || rollenFragenDefs.crsRelevant;
      rollenResultSetFragen.fatcaGMSG = rollenResultSetFragen.fatcaGMSG || rollenFragenDefs.fatcaGMSG;
      rollenResultSetFragen.veranlagungBeratung =
        rollenResultSetFragen.veranlagungBeratung || rollenFragenDefs.veranlagungBeratung;
      rollenResultSetFragen.schriftformDatennutzung =
        rollenResultSetFragen.schriftformDatennutzung || rollenFragenDefs.schriftformDatennutzung;
      rollenResultSetFragen.bestattung = rollenResultSetFragen.bestattung || rollenFragenDefs.bestattung;
      rollenResultSetFragen.familienstand = rollenResultSetFragen.familienstand || rollenFragenDefs.familienstand;
      rollenResultSetFragen.geburtsland = rollenResultSetFragen.geburtsland || rollenFragenDefs.geburtsland;
      rollenResultSetFragen.berufsinfo = rollenResultSetFragen.berufsinfo || rollenFragenDefs.berufsinfo;
      rollenResultSetFragen.sozialVersDaten = rollenResultSetFragen.sozialVersDaten || rollenFragenDefs.sozialVersDaten;
      rollenResultSetFragen.adresse = rollenResultSetFragen.adresse || rollenFragenDefs.adresse;
      rollenResultSetFragen.nationalitaet = rollenResultSetFragen.nationalitaet || rollenFragenDefs.nationalitaet;
      rollenResultSetFragen.erhaltbasisInfoBlatt =
        rollenResultSetFragen.erhaltbasisInfoBlatt || rollenFragenDefs.erhaltbasisInfoBlatt;
    }
  });
  // juristische Person mitberücksichtigen
  rollenResultSetFragen.familienstand = rollenResultSetFragen.familienstand && !juristischePerson;
  rollenResultSetFragen.geburtsland = rollenResultSetFragen.geburtsland && !juristischePerson;
  rollenResultSetFragen.berufsinfo = rollenResultSetFragen.berufsinfo && !juristischePerson;
  rollenResultSetFragen.sozialVersDaten = rollenResultSetFragen.sozialVersDaten && !juristischePerson;
  rollenResultSetFragen.adresse = rollenResultSetFragen.adresse && !juristischePerson;
  rollenResultSetFragen.nationalitaet = rollenResultSetFragen.nationalitaet && !juristischePerson;
  return rollenResultSetFragen;
}

export function getPersonRollenAsArray(person: Person): string[] {
  const personenRollen = new Set<string>();
  if (person.personRollen) {
    if (person.personRollen.vn) {
      personenRollen.add(PartnerRefAngebotBeziehungRollen.VERSICHERUNGSNEHMER);
    }
    if (person.personRollen.vn2) {
      personenRollen.add(PartnerRefAngebotBeziehungRollen.VERSICHERUNGSNEHMER_2);
    }
    if (person.personRollen.vp) {
      personenRollen.add(PartnerRefAngebotBeziehungRollen.VERSICHERTE_PERSON_1);
    }
    if (person.personRollen.vp2) {
      personenRollen.add(PartnerRefAngebotBeziehungRollen.VERSICHERTE_PERSON_2);
    }
    if (person.personRollen.praemienzahler) {
      personenRollen.add(PartnerRefAngebotBeziehungRollen.PRAEMIENZAHLER);
    }
    if (person.personRollen.gesetzlicherVertreter && person.personRollen.gesetzlicherVertreter.length > 0) {
      personenRollen.add(PartnerRefAngebotBeziehungRollen.GESETZLICHER_VERTRETER);
    }
    if (person.personRollen.treugeber && person.personRollen.treugeber.length > 0) {
      personenRollen.add(PartnerRefAngebotBeziehungRollen.TREUGEBER);
    }
  }
  return Array.from(personenRollen);
}

export function getZusatzFragenSonderbehandlungForPerson(person: Person): IZusatzFlagsFuerSonderBehandlung {
  return isPersonInSparte(person, 'L')
    ? getZusatzFragenSonderbehandlung(
        person.tarife,
        getPersonRollenAsArray(person),
        getLvDefinitionen(lvTarifFeatures, lvRollenFeatures),
        person && person.juristischePerson
      )
    : {};
}

export function getFragenBlockDefsForPerson(antrag: Antragdata, person: Person): IAntragFragen {
  const juristischePerson = person?.juristischePerson;
  const personRollen = getPersonRollenAsArray(person);
  const antragFragenProps: string[] = Object.getOwnPropertyNames(new IAntragFragen());
  const resultFragen: IAntragFragen = {};

  const gvFragenDefs: IAntragFragen = isPersonInSparte(person, AngebotSparte.G)
    ? getFragenBlockDefinitionForPersonRollenGv(personRollen, juristischePerson)
    : {};

  const uvFragenDefs: IAntragFragen = isPersonInSparte(person, AngebotSparte.U)
    ? getFragenBlockDefinitionForPersonRollenUv(personRollen, juristischePerson)
    : {};

  const svFragenDefs: IAntragFragen = isPersonInSparte(person, AngebotSparte.S)
    ? getFragenBlockDefinitionForPersonRollenSv(personRollen, juristischePerson)
    : {};

  const lvFragenDefs: IAntragFragen = hatAntragSpartenAngebotLv(antrag)
    ? getFragenBlockDefinitionLv(
        person.tarife,
        personRollen,
        getLvDefinitionen(lvTarifFeatures, lvRollenFeatures),
        juristischePerson
      )
    : {};

  antragFragenProps.forEach(
    (key) => (resultFragen[key] = gvFragenDefs[key] || uvFragenDefs[key] || svFragenDefs[key] || lvFragenDefs[key])
  );
  return resultFragen;
}

export function getFragenDefsForTarif(tarifschluessel: string, tariffeatures: ITarifFeature[]): IAntragFragen {
  const tarifDefs = tariffeatures.find((tarif) => tarif.tarifNr === tarifschluessel);
  const tarifFragenDefs = tarifDefs ? tarifDefs.fragenDefs : null;
  return tarifFragenDefs;
}

export function getFragenDefsForLvTarif(tarifschluessel: string): IAntragFragen {
  return getFragenDefsForTarif(tarifschluessel, lvTarifFeatures);
}

export function getFragenBlockDefsForGesundheitsfragenGruppePerson(
  person: Person,
  loggingStreamId?: string
): Set<string> {
  const gesundheitsfragenGruppe = new Set<string>();
  bufferedLogger.log(
    loggingStreamId,
    `[getFragenBlockDefsForGesundheitsfragenGruppePerson] - person.angebotsBeziehung -> ${JSON.stringify(person.angebotsBeziehung, null, 2)}`
  );

  if (person.angebotsBeziehung && person.angebotsBeziehung.length > 0) {
    person.angebotsBeziehung.forEach((beziehung) => {
      if (isVersichertePersonInAngebotsBeziehung(beziehung)) {
        if (beziehung.pflegeVS) {
          gesundheitsfragenGruppe.add(PFLEGE_VS_GESUNDHEITSFRAGENG);
        }
        if (beziehung.gpzBaustein) {
          gesundheitsfragenGruppe.add(GPZ_BAUSTEIN_GESUNDHEITSFRAGENG);
        }
        if (beziehung.erlebenMitFragen) {
          gesundheitsfragenGruppe.add(ERLEBEN_MIT_FRAGEN);
        }
        if (beziehung.pbzBaustein) {
          gesundheitsfragenGruppe.add(PBZ_BAUSTEIN_GESUNDHEITSFRAGENG);
        }
        if (beziehung.utzBaustein) {
          gesundheitsfragenGruppe.add(UTZ_BAUSTEIN_GESUNDHEITSFRAGENG);
        }
        if (beziehung.sparte === 'G') {
          gesundheitsfragenGruppe.add(SPARTE_GV_RELEVANT);
        }
        if (beziehung.sparte === 'U') {
          gesundheitsfragenGruppe.add(SPARTE_UV_RELEVANT);
        }

        if (
          (beziehung.tarifSchluessel && beziehung.sparte === 'L') ||
          (!beziehung.sparte && beziehung.tarifSchluessel)
        ) {
          getFragenDefsForLvTarif(beziehung.tarifSchluessel).gesundheitsfragenG.forEach((gruppe) => {
            gesundheitsfragenGruppe.add(gruppe);
          });
        }
      }
    });
  }
  bufferedLogger.log(
    loggingStreamId,
    `[getFragenBlockDefsForGesundheitsfragenGruppePerson] - Returning: ${JSON.stringify(gesundheitsfragenGruppe, null, 2)}`
  );

  return gesundheitsfragenGruppe;
}

export function isVersichertePersonInAngebotsBeziehung(angebotsBeziehung: any): boolean {
  // angebotsBeziehung.angebotsRollen wird aus Gründen der Backwertskompatibilität abgefragt
  // TODO Typangabe im Übergabeparameter
  const rollen: string[] = angebotsBeziehung.angebotsRollen
    ? angebotsBeziehung.angebotsRollen.rollen
    : angebotsBeziehung.rollen;
  return (
    rollen &&
    (rollen.includes(PartnerRefAngebotBeziehungRollen.VERSICHERTE_PERSON_1) ||
      rollen.includes(PartnerRefAngebotBeziehungRollen.VERSICHERTE_PERSON_2))
  );
}

export function fillRollenDefinitionen(
  lvDefinition: ILvDefinitionen,
  lvRollenDefs: IRollenFeatures[]
): ILvDefinitionen {
  lvRollenDefs.forEach((rollenDef) => (lvDefinition.rollen[rollenDef.rolle] = rollenDef));
  return lvDefinition;
}

export function fillTarifDefinitionen(lvDefinition: ILvDefinitionen, lvTarifDefs: ITarifFeature[]): ILvDefinitionen {
  lvTarifDefs.forEach((tarifDef) => (lvDefinition.tarife[tarifDef.tarifNr] = tarifDef));
  return lvDefinition;
}

export function getLvDefinitionen(lvTarifDefs: ITarifFeature[], lvRollenDefs: IRollenFeatures[]): ILvDefinitionen {
  const lvDefinition: ILvDefinitionen = { tarife: {}, rollen: {} };
  fillTarifDefinitionen(lvDefinition, lvTarifDefs);
  fillRollenDefinitionen(lvDefinition, lvRollenDefs);
  return lvDefinition;
}
